// src/components/VideoUploader.jsx
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch } from "react-redux";
import { setIsLoading } from "../app/Actions/cmsAction";
import "./css/VideoUploader.css";
import { processVideo } from "../services/videoProcessing";
import { useGoogleDrivePicker } from "../services/googleDriveService";
import { useOneDrivePicker } from "../services/onedriveService";
import { useDropboxPicker } from "../services/dropboxService";
import UploadService from "../services/uploadService";

const VideoUploader = ({ onProcessingStart, onProcessingComplete }) => {
  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedSource, setSelectedSource] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [s3FileInfo, setS3FileInfo] = useState(null);
  const [resumableUploads, setResumableUploads] = useState([]);
  const dropzoneRef = useRef(null);

  // Initialize Google Drive, OneDrive, and Dropbox pickers
  const { openPicker: openGoogleDrivePicker } = useGoogleDrivePicker(dispatch);
  const { openPicker: openOneDrivePicker } = useOneDrivePicker(dispatch);
  const { openPicker: openDropboxPicker } = useDropboxPicker(dispatch);

  // Check for resumable uploads on component mount
  useEffect(() => {
    // Look for saved upload states in local storage
    const savedUploads = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith("multipart_upload_")) {
        try {
          const uploadState = JSON.parse(localStorage.getItem(key));
          if (uploadState && uploadState.file_name) {
            savedUploads.push(uploadState);
          }
        } catch (error) {
          console.error("Error parsing saved upload state:", error);
        }
      }
    }

    if (savedUploads.length > 0) {
      setResumableUploads(savedUploads);
    }
  }, []);

  // Handle local file selection
  const handleLocalFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.includes("video")) {
      setFile(selectedFile);
      setFileInfo(null);
      setSelectedSource("local");
      setS3FileInfo(null);
    } else if (selectedFile) {
      alert("Please select a valid video file.");
    }
  };

  // Handle Google Drive file selection
  const handleGoogleDriveSelect = useCallback((files) => {
    if (files && files.length > 0) {
      // In real implementation, you might need to download the file from Google Drive first
      setFileInfo(files[0]);
      setFile(null);
      setSelectedSource("gdrive");
      setS3FileInfo(null);

      // For Google Drive, we might want to convert the file to a blob
      // to upload it directly to S3 instead of using server-side download
      fetchGoogleDriveFile(files[0]);
    }
  }, []);

  // Fetch and convert Google Drive file to a Blob for direct upload
  const fetchGoogleDriveFile = async (driveFile) => {
    try {
      // Set loading state while downloading
      console.log("Fetching Google Drive file:", driveFile);

      // Use the access token to download the file directly
      if (driveFile.accessToken) {
        const response = await fetch(
          `https://www.googleapis.com/drive/v3/files/${driveFile.id}?alt=media`,
          {
            headers: {
              Authorization: `Bearer ${driveFile.accessToken}`,
            },
          }
        );

        if (response.ok) {
          dispatch(setIsLoading(true));
          const blob = await response.blob();
          // Create a File object from the blob
          const file = new File([blob], driveFile.name, {
            type: driveFile.mimeType || "video/mp4",
          });
          setFile(file);
          console.log("Downloaded Google Drive file:", file);
        } else {
          throw new Error(`Failed to download file: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("Error fetching Google Drive file:", error);
      alert("Error downloading file from Google Drive: " + error.message);
    } finally {
      // Clear loading state when complete
      dispatch(setIsLoading(false));
    }
  };

  // Handle OneDrive file selection
  const handleOneDriveSelect = useCallback((files) => {
    if (files && files.length > 0) {
      setFileInfo(files[0]);
      setFile(null);
      setSelectedSource("onedrive");
      setS3FileInfo(null);

      // Similar to Google Drive, we can try to fetch the file
      fetchOneDriveFile(files[0]);
    }
  }, []);

  // Fetch OneDrive file
  const fetchOneDriveFile = async (oneDriveData) => {
    try {
      // Set loading state before attempting download
      dispatch(setIsLoading(true));
      
      if (!oneDriveData) {
        throw new Error("No file data provided");
      }
      
      const downloadUrl = oneDriveData.downloadUrl;
      
      if (!downloadUrl) {
        throw new Error("Download URL is missing or invalid");
      }
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Use the provided mimeType if available
      const mimeType = oneDriveData.mimeType || getMimeType(oneDriveData.name) || "video/mp4";
      
      const file = new File([blob], oneDriveData.name, {
        type: mimeType,
      });
      
      setFile(file);
      return file; // Return the file for chaining if needed
      
    } catch (error) {
      console.error("Error fetching OneDrive file:", error);
      alert("Error downloading file from OneDrive: " + error.message);
      throw error; // Re-throw to allow handling upstream
    } finally {
      // Clear loading state when complete
      dispatch(setIsLoading(false));
    }
  };

  // Helper function to determine MIME type based on file extension
  const getMimeType = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    const mimeTypes = {
      mp4: "video/mp4",
      mov: "video/quicktime",
      avi: "video/x-msvideo",
      wmv: "video/x-ms-wmv",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      // Add more MIME types as needed
    };

    return mimeTypes[extension] || null;
  };

  // Handle Dropbox file selection
  const handleDropboxSelect = useCallback((files) => {
    if (files && files.length > 0) {
      setFileInfo(files[0]);
      setFile(null);
      setSelectedSource("dropbox");
      setS3FileInfo(null);

      // Fetch Dropbox file
      fetchDropboxFile(files[0]);
    }
  }, []);

  // Fetch Dropbox file
  const fetchDropboxFile = async (dropboxFile) => {
    try {
      // Set loading state while downloading

      if (dropboxFile.link) {
        const response = await fetch(dropboxFile.link);
        if (response.ok) {
          dispatch(setIsLoading(true));

          const blob = await response.blob();
          const file = new File([blob], dropboxFile.name, {
            type: dropboxFile.mimeType || "video/mp4",
          });
          setFile(file);
        } else {
          throw new Error(`Failed to download file: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error("Error fetching Dropbox file:", error);
      alert("Error downloading file from Dropbox: " + error.message);
    } finally {
      // Clear loading state when complete
      dispatch(setIsLoading(false));
    }
  };

  // Open Google Drive picker
  const handleGoogleDriveClick = useCallback(() => {
    openGoogleDrivePicker({ onSelect: handleGoogleDriveSelect });
  }, [openGoogleDrivePicker, handleGoogleDriveSelect]);

  // Open OneDrive picker
  const handleOneDriveClick = useCallback(() => {
    openOneDrivePicker({ onSelect: handleOneDriveSelect });
  }, [openOneDrivePicker, handleOneDriveSelect]);

  // Open Dropbox picker
  const handleDropboxClick = useCallback(() => {
    openDropboxPicker({ onSelect: handleDropboxSelect });
  }, [openDropboxPicker, handleDropboxSelect]);

  // Drag and drop handlers for dropzone
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.type.includes("video")) {
        setFile(droppedFile);
        setFileInfo(null);
        setSelectedSource("local");
        setS3FileInfo(null);
      } else {
        alert("Please drop a valid video file.");
      }
    }
  };

  // Upload file to S3 using multipart upload
  const uploadToS3Multipart = async (videoFile) => {
    try {
      if (!videoFile) {
        throw new Error("No file available for upload");
      }

      // Set global loading state for upload start
      dispatch(setIsLoading(true));

      // For all sources, we now use multipart upload
      return await UploadService.uploadFile(
        videoFile,
        (progress) => setUploadProgress(progress),
        selectedSource === "local" ? "direct_upload" : selectedSource
      );
    } catch (error) {
      console.error("Error uploading to S3:", error);
      throw new Error(`Failed to upload to S3: ${error.message}`);
    } finally {
      // Clear global loading state when upload completes or fails
      dispatch(setIsLoading(false));
    }
  };

  // Handle resuming an upload
  const handleResumeUpload = async (savedUpload) => {
    try {
      // Set loading state
      dispatch(setIsLoading(true));

      // Try to find the file with the same name in the file system
      // This would typically be handled by showing a file picker
      const result = await window.showOpenFilePicker({
        types: [
          {
            description: "Video Files",
            accept: {
              "video/*": [".mp4", ".mov", ".avi", ".webm"],
            },
          },
        ],
      });

      if (result && result.length > 0) {
        const fileHandle = result[0];
        const fileObj = await fileHandle.getFile();

        // Check if this is the same file
        if (fileObj.name === savedUpload.file_name) {
          setFile(fileObj);
          setSelectedSource("local");
          setIsUploading(true);
          setUploadProgress(0);

          // Resume the upload
          const uploadResult = await UploadService.resumeMultipartUpload(
            fileObj,
            savedUpload.file_id,
            savedUpload.upload_id,
            savedUpload.completed_parts,
            (progress) => setUploadProgress(progress)
          );

          setS3FileInfo(uploadResult);
          UploadService.clearUploadState(savedUpload.file_id);

          setIsUploading(false);
          setUploadProgress(100);

          // Remove from resumable uploads list
          setResumableUploads((prevUploads) =>
            prevUploads.filter(
              (upload) => upload.file_id !== savedUpload.file_id
            )
          );
        } else {
          alert(
            "The selected file does not match the previously uploaded file."
          );
        }
      }
    } catch (error) {
      console.error("Error resuming upload:", error);
      alert("Failed to resume upload: " + error.message);
    } finally {
      // Clear loading state
      dispatch(setIsLoading(false));
    }
  };

  // Process video from any source
  const handleUpload = async () => {
    if (!file) {
      alert("Please select a video file first.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    onProcessingStart();
    dispatch(setIsLoading(true)); // Set global loading state

    try {
      // Upload the file to S3 using multipart upload
      const uploadResult = await uploadToS3Multipart(file);
      setS3FileInfo(uploadResult);
      // checkUploadStatus(uploadResult.fileId);
      // // Start processing with the S3 URL
      // let fileMetadata = {
      //   name: file.name,
      //   size: file.size,
      //   type: file.type,
      // };

      // // Process the video with the S3 URL
      // const viralClips = await processVideo(fileMetadata, uploadResult.s3_url);

      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(100);
        // onProcessingComplete(viralClips);
        dispatch(setIsLoading(false)); // Clear global loading state
      }, 1000);
    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      alert("Error processing video: " + error.message);
      dispatch(setIsLoading(false)); // Clear global loading state on error
    }
  };

  // Get the filename to display
  const getDisplayFilename = () => {
    if (file) return file.name;
    if (fileInfo) return fileInfo.name;
    return "No file selected";
  };

  // Check upload status
  const checkUploadStatus = async (fileId) => {
    try {
      dispatch(setIsLoading(true)); // Set loading state
      const status = await UploadService.getFileStatus(fileId);
      console.log("File status:", status);
      return status;
    } catch (error) {
      console.error("Error checking file status:", error);
      return null;
    } finally {
      dispatch(setIsLoading(false)); // Clear loading state
    }
  };

  return (
    <div className="video-uploader">
      <div className="source-buttons">
        <div
          className={`source-button ${
            selectedSource === "local" ? "selected" : ""
          }`}
          onClick={() => document.getElementById("local-file-input").click()}
        >
          <span className="source-icon">💻</span>
          <span className="source-name">Local File</span>
          <input
            id="local-file-input"
            type="file"
            onChange={handleLocalFileChange}
            accept="video/*"
            style={{ display: "none" }}
          />
        </div>
        <div
          className={`source-button ${
            selectedSource === "gdrive" ? "selected" : ""
          }`}
          onClick={handleGoogleDriveClick}
        >
          <span className="source-icon">📁</span>
          <span className="source-name">Google Drive</span>
        </div>
        <div
          className={`source-button ${
            selectedSource === "onedrive" ? "selected" : ""
          }`}
          onClick={handleOneDriveClick}
        >
          <span className="source-icon">☁️</span>
          <span className="source-name">OneDrive</span>
        </div>
        <div
          className={`source-button ${
            selectedSource === "dropbox" ? "selected" : ""
          }`}
          onClick={handleDropboxClick}
        >
          <span className="source-icon">📦</span>
          <span className="source-name">Dropbox</span>
        </div>
      </div>

      {resumableUploads.length > 0 && (
        <div className="resumable-uploads">
          <h3>Resumable Uploads</h3>
          <div className="resumable-list">
            {resumableUploads.map((upload) => (
              <div key={upload.file_id} className="resumable-item">
                <span className="resumable-name">{upload.file_name}</span>
                <button
                  className="resume-button"
                  onClick={() => handleResumeUpload(upload)}
                >
                  Resume Upload
                </button>
                <button
                  className="discard-button"
                  onClick={() => {
                    UploadService.clearUploadState(upload.file_id);
                    setResumableUploads((prev) =>
                      prev.filter((u) => u.file_id !== upload.file_id)
                    );
                  }}
                >
                  Discard
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        className={`dropzone ${isDragging ? "active" : ""}`}
        ref={dropzoneRef}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <div className="dropzone-content">
          <span className="dropzone-icon">📥</span>
          <p className="dropzone-text">Drag & drop your video file here</p>
          <p className="dropzone-subtext">or use one of the options above</p>
        </div>
      </div>

      <div className="file-display">
        <div className="file-info-container">
          <span className="file-icon">
            {selectedSource === "gdrive"
              ? "📁"
              : selectedSource === "onedrive"
              ? "☁️"
              : selectedSource === "dropbox"
              ? "📦"
              : selectedSource === "local"
              ? "📂"
              : "🎬"}
          </span>
          <span className="file-name">{getDisplayFilename()}</span>
          {(file || fileInfo) && (
            <button
              className="clear-file-button"
              onClick={() => {
                setFile(null);
                setFileInfo(null);
                setSelectedSource(null);
                setS3FileInfo(null);
              }}
            >
              ✖
            </button>
          )}
        </div>
      </div>

      {file && (
        <div className="video-preview">
          <video controls width="100%">
            <source src={URL.createObjectURL(file)} type={file.type} />
            Your browser does not support the video tag.
          </video>
          <div className="video-info">
            <p>Size: {formatFileSize(file.size)}</p>
            <p>Type: {file.type}</p>
          </div>
        </div>
      )}

      {isUploading ? (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <div className="progress-text">{uploadProgress}% Uploaded</div>
        </div>
      ) : (
        <button
          className="generate-button"
          onClick={handleUpload}
          disabled={!file || isUploading}
        >
          <span className="button-text">Upload and Generate Viral Clips</span>
          <span className="button-icon">✨</span>
        </button>
      )}

      <div className="ai-features">
        <div className="feature-item">
          <span className="feature-icon">🧠</span>
          <span className="feature-text">Content Analysis</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">👁️</span>
          <span className="feature-text">Visual Recognition</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">🔄</span>
          <span className="feature-text">Smart Recommendations</span>
        </div>
        <div className="feature-item">
          <span className="feature-icon">📱</span>
          <span className="feature-text">Multiple Platforms</span>
        </div>
      </div>
    </div>
  );
};

export default VideoUploader;
